import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard,
  CalendarRange,
  CalendarDays,
  Users,
  Briefcase,
  HardHat,
  FileText,
  Package,
  BarChart3,
  Megaphone,
  Smartphone,
  Settings,
  HelpCircle,
  Workflow,
  KanbanSquare,
  Bot,
  CreditCard,
  MessageSquare,
  Inbox,
  Zap,
  CheckSquare,
  PhoneCall,
  Menu,
  X,
  Clock,
  MapPin,
  LogOut,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import { usePlatformBranding, resolveBrandAsset } from "../contexts/PlatformBrandingContext";
import { useHelpChat } from "../contexts/HelpChatContext";
import { useLocationShare } from "../contexts/LocationShareContext";
import { useTimeClock, fmtDuration, sessionSeconds } from "./TimeClock";
import { Switch } from "./ui/switch";
import { pricebookApi, tasksApi, jobsApi, inboxApi } from "../lib/api";
import { isLowStock } from "../pages/Pricebook";

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

// Each item declares which roles see it. `null` = visible to all logged-in roles.
// `perm` (optional) ties the item to a module permission — hidden when the user's
// permission type doesn't grant it (admins always pass).
const navGroups = [
  {
    title: "Operate",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "dispatcher"], perm: "dashboard.view" },
      { to: "/messages", label: "Messages", icon: MessageSquare, roles: ["admin", "dispatcher", "technician"], module: "messaging" },
      { to: "/inbox", label: "Inbox", icon: Inbox, roles: ["admin", "dispatcher"], module: "inbox" },
      { to: "/dispatch", label: "Dispatch Board", icon: CalendarRange, roles: ["admin", "dispatcher"], perm: "dispatch.view", module: "dispatch" },
      { to: "/jobs", label: "Jobs", icon: Briefcase, roles: ["admin", "dispatcher", "technician"], perm: "jobs.view" },
      { to: "/tasks", label: "Tasks", icon: CheckSquare, roles: ["admin", "dispatcher", "technician"], perm: "tasks.view" },
      { to: "/calendar", label: "Calendar", icon: CalendarDays, roles: ["admin", "dispatcher", "technician", "office_personnel"], module: "calendar" },
      { to: "/customers", label: "Customers", icon: Users, roles: ["admin", "dispatcher", "technician"], perm: "customers.view" },
      { to: "/history", label: "Job History", icon: Clock, roles: ["admin", "dispatcher", "office_personnel", "technician"] },
    ],
  },
  {
    title: "Revenue",
    items: [
      { to: "/estimates", label: "Estimates & Invoices", icon: FileText, roles: ["admin", "dispatcher", "technician"], perm: "estimates.view" },
      { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, roles: ["admin", "dispatcher", "technician", "office_personnel"], module: "pipeline" },
      { to: "/pricebook", label: "Pricebook & Inventory", icon: Package, roles: ["admin", "dispatcher", "technician"], perm: "pricebook.view", module: "pricebook" },
    ],
  },
  {
    title: "Grow",
    items: [
      { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "dispatcher"], perm: "reports.view", module: "reports" },
      { to: "/marketing", label: "Marketing", icon: Megaphone, roles: ["admin", "dispatcher"], perm: "marketing.view", module: "marketing" },
      { to: "/automations", label: "Automations", icon: Zap, roles: ["admin", "dispatcher"], perm: "automations.view", module: "automations" },
      { to: "/mobile", label: "Mobile Tech", icon: Smartphone, roles: ["admin", "dispatcher", "technician"], module: "mobile_tech" },
    ],
  },
  {
    title: "AI Tools",
    items: [
      { to: "/flowchart", label: "Flow Chart", icon: Workflow, roles: ["admin", "dispatcher"], module: "flow_ai" },
      { to: "/guru", label: "Diagnostic Guru", icon: Bot, badge: "AI", roles: ["admin", "dispatcher", "technician"], module: "guru" },
      { to: "/ai-agents", label: "AI Voice Agents", icon: PhoneCall, badge: "AI", roles: ["admin", "dispatcher"], module: "ai_agents" },
    ],
  },
  {
    title: "Admin",
    items: [
      { to: "/technicians", label: "Staff", icon: HardHat, roles: ["admin", "dispatcher"], perm: "staff.view" },
      { to: "/settings/billing", label: "Billing & Subscription", icon: CreditCard, roles: ["admin"] },
      { to: "/settings/company", label: "Company Settings", icon: Settings, roles: ["admin"], perm: "settings.view" },
    ],
  },
];

export default function Sidebar() {
  const { user, can, hasModule, logout } = useAuth();
  const { branding } = usePlatformBranding();
  const { toggle: toggleHelpChat } = useHelpChat();
  const chat = useChat();
  const navigate = useNavigate();
  const messagesUnread = chat?.unread?.total || 0;
  const role = user && user !== false ? user.role : null;
  const [lowStockCount, setLowStockCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [unassignedJobs, setUnassignedJobs] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Clock in/out + share-location — technician-only desktop/tablet sidebar
  // controls, mirroring the mobile drawer's ClockMenu behavior 1:1.
  const isTechnician = role === "technician";
  const canTimeclock = isTechnician && hasModule("timeclock");
  const clock = useTimeClock(canTimeclock);
  const locationShare = useLocationShare();
  const [nowMs, setNowMs] = useState(Date.now());
  const clockStatus = clock.state?.status || "clocked_out";
  const isClockedIn = clockStatus === "clocked_in" || clockStatus === "on_lunch";
  useEffect(() => {
    if (!canTimeclock || !isClockedIn) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [canTimeclock, isClockedIn]);

  const onSidebarLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const toggleShareLocation = async (val) => {
    if (val) {
      const ok = await locationShare.enable();
      if (!ok) toast.error("Location permission is required to share your location.");
    } else {
      locationShare.disable();
    }
  };

  // Open/close the mobile nav drawer when the Topbar hamburger is tapped.
  useEffect(() => {
    const toggle = () => setMobileOpen((o) => !o);
    window.addEventListener("toggle-mobile-nav", toggle);
    return () => window.removeEventListener("toggle-mobile-nav", toggle);
  }, []);

  // Poll the open-tasks alert count for the Tasks sidebar badge.
  // Admins see the company-wide open count; staff see tasks assigned to them.
  useEffect(() => {
    if (!role || role === "super_admin") return;
    let cancelled = false;
    const reload = () =>
      tasksApi
        .badge()
        .then((res) => { if (!cancelled) setTaskCount(res?.count || 0); })
        .catch(() => { /* silent — non-critical */ });
    reload();
    const handler = () => reload();
    window.addEventListener("tasks-changed", handler);
    const interval = setInterval(reload, 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener("tasks-changed", handler);
      clearInterval(interval);
    };
  }, [role]);

  // Fetch low-stock count for the Pricebook sidebar badge.
  // Admin + dispatcher only (tech doesn't see the badge but can still navigate to pricebook).
  useEffect(() => {
    if (role !== "admin" && role !== "dispatcher") return;
    let cancelled = false;
    const reload = () =>
      pricebookApi
        .list()
        .then((items) => {
          if (cancelled) return;
          setLowStockCount(items.filter((i) => isLowStock(i)).length);
        })
        .catch(() => { /* silent — non-critical */ });
    reload();
    const handler = () => reload();
    window.addEventListener("pricebook-stock-changed", handler);
    const interval = setInterval(reload, 60_000); // soft refresh every minute
    return () => {
      cancelled = true;
      window.removeEventListener("pricebook-stock-changed", handler);
      clearInterval(interval);
    };
  }, [role]);

  // Unassigned-jobs count for the Dispatch Board sidebar badge (admin + dispatcher).
  useEffect(() => {
    if (role !== "admin" && role !== "dispatcher") return;
    let cancelled = false;
    const reload = () =>
      jobsApi
        .unassignedCount()
        .then((res) => { if (!cancelled) setUnassignedJobs(res?.count || 0); })
        .catch(() => { /* silent — non-critical */ });
    reload();
    const handler = () => reload();
    window.addEventListener("jobs-changed", handler);
    const interval = setInterval(reload, 60_000);
    return () => {
      cancelled = true;
      window.removeEventListener("jobs-changed", handler);
      clearInterval(interval);
    };
  }, [role]);


  // Unified Inbox unread count badge (admin + dispatcher, only if entitled).
  useEffect(() => {
    if ((role !== "admin" && role !== "dispatcher") || !hasModule("inbox")) return;
    let cancelled = false;
    const reload = () =>
      inboxApi
        .unread()
        .then((res) => { if (!cancelled) setInboxUnread(res?.total || 0); })
        .catch(() => { /* silent — non-critical */ });
    reload();
    const handler = () => reload();
    window.addEventListener("inbox-changed", handler);
    const interval = setInterval(reload, 30_000);
    return () => {
      cancelled = true;
      window.removeEventListener("inbox-changed", handler);
      clearInterval(interval);
    };
  }, [role, hasModule]);


  const visibleGroups = navGroups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) => (!role || !i.roles || i.roles.includes(role)) && (!i.perm || can(i.perm))
      ),
    }))
    .filter((g) => g.items.length > 0);

  const navBody = (onNavigate, mobile = false) => (
    <>
      <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2.5">
        <div className="h-[43px] w-[43px] rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-lg shadow-blue-900/40">
          <img src={resolveBrandAsset(branding.sidebarLogoUrl) || "/brand-logo.png"} alt={branding.brandName} className="h-full w-full object-contain" data-testid="sidebar-brand-logo" />
        </div>
        <div className="min-w-0">
          <div className="text-white font-bold text-[15px] tracking-tight leading-tight" data-testid="sidebar-brand-name">{branding.brandName}</div>
          {user && user !== false && user.company?.name ? (
            <div className="text-[11px] text-slate-300 font-medium leading-tight truncate max-w-[150px]" data-testid="sidebar-company-name">{user.company.name}</div>
          ) : (
            <div className="text-[10px] text-slate-400 uppercase tracking-widest">{branding.tagline}</div>
          )}
        </div>
      </div>

      {!mobile && isTechnician && (
        <div className="border-b border-white/10 px-2.5 py-2.5 space-y-2" data-testid="sidebar-tech-controls">
          <div className="flex items-center justify-between px-1">
            <span className="text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-500">Time Clock</span>
            {canTimeclock && (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold" data-testid="sidebar-clock-status">
                <span className={`h-1 w-1 rounded-full ${isClockedIn ? "bg-emerald-400" : "bg-rose-400"}`} />
                <span className={isClockedIn ? "text-emerald-400" : "text-rose-400"}>{isClockedIn ? "Clocked in" : "Clocked out"}</span>
              </span>
            )}
          </div>
          {canTimeclock ? (
            <>
              <div className="px-1 text-[9px] text-slate-400" data-testid="sidebar-clock-info">
                {clock.loading
                  ? "Loading…"
                  : isClockedIn
                  ? `Since ${fmtTime(clock.state?.entry?.clockIn)} · ${fmtDuration(sessionSeconds(clock.state?.entry, nowMs))} so far`
                  : clock.state && clock.state.todaySeconds > 0
                  ? `Today: ${fmtDuration(clock.state.todaySeconds)} worked`
                  : "Not clocked in yet today"}
              </div>
              <button
                onClick={() => (isClockedIn ? clock.clockOut() : clock.clockIn())}
                disabled={clock.busy || clock.loading}
                data-testid="sidebar-clock-toggle-btn"
                className={`w-full h-7 rounded-md text-[10.5px] font-semibold text-white disabled:opacity-60 transition-colors ${isClockedIn ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              >
                {isClockedIn ? "Clock Out" : "Clock In"}
              </button>
              <div className="flex items-center justify-between px-1 pt-0.5">
                <span className="flex items-center gap-1.5 text-[10.5px] font-medium text-slate-300">
                  <MapPin className="h-3 w-3 text-slate-400" /> Share Location
                </span>
                <div className="scale-[0.8] origin-right">
                  <Switch
                    checked={locationShare.enabled}
                    onCheckedChange={toggleShareLocation}
                    disabled={locationShare.busy}
                    data-testid="sidebar-share-location-toggle"
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="px-1 text-[9px] text-slate-500" data-testid="sidebar-clock-unavailable">Not available on your plan</div>
          )}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid="sidebar-nav">
        {visibleGroups.map((group) => (
          <div key={group.title}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 px-3 mb-2">
              {group.title}
            </div>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const locked = item.module && !hasModule(item.module);
                if (locked) {
                  return (
                    <li key={item.to}>
                      <div
                        data-testid={`sidebar-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        title="Not in your plan — upgrade to unlock"
                        className="group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] font-medium text-slate-500 cursor-default select-none"
                      >
                        <Icon className="h-[18px] w-[18px] text-slate-600" strokeWidth={2} />
                        <span className="flex-1">{item.label}</span>
                        <Link
                          to="/settings/billing"
                          onClick={onNavigate}
                          data-testid={`sidebar-upgrade-${item.module}`}
                          className="text-[10px] font-bold uppercase tracking-wide text-amber-400 hover:text-amber-300 hover:underline"
                        >
                          upgrade
                        </Link>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={item.to}>
                    <NavLink
                      to={mobile && item.to === "/calendar" ? "/mobile/calendar" : item.to}
                      end={item.end}
                      data-testid={`sidebar-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] font-medium transition-colors duration-150 ${
                          isActive
                            ? "bg-white/10 text-white"
                            : "text-slate-300 hover:bg-white/5 hover:text-white"
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            className={`h-[18px] w-[18px] ${isActive ? "text-blue-400" : "text-slate-400 group-hover:text-slate-200"}`}
                            strokeWidth={2}
                          />
                          <span className="flex-1">{item.label}</span>
                          {item.to === "/messages" && messagesUnread > 0 && (
                            <span
                              data-testid="sidebar-messages-unread-badge"
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white shadow-sm shadow-rose-900/40"
                            >
                              {messagesUnread > 99 ? "99+" : messagesUnread}
                            </span>
                          )}
                          {item.to === "/inbox" && inboxUnread > 0 && (
                            <span
                              data-testid="sidebar-inbox-unread-badge"
                              title={`${inboxUnread} unread customer message${inboxUnread === 1 ? "" : "s"}`}
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white shadow-sm shadow-rose-900/40"
                            >
                              {inboxUnread > 99 ? "99+" : inboxUnread}
                            </span>
                          )}
                          {item.to === "/tasks" && taskCount > 0 && (
                            <span
                              data-testid="sidebar-tasks-alert-badge"
                              title={`${taskCount} open task${taskCount === 1 ? "" : "s"}`}
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white shadow-sm shadow-rose-900/40"
                            >
                              {taskCount > 99 ? "99+" : taskCount}
                            </span>
                          )}
                          {item.to === "/dispatch" && unassignedJobs > 0 && (
                            <span
                              data-testid="sidebar-dispatch-unassigned-badge"
                              title={`${unassignedJobs} unassigned job${unassignedJobs === 1 ? "" : "s"} waiting to be scheduled`}
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white shadow-sm shadow-rose-900/40"
                            >
                              {unassignedJobs > 99 ? "99+" : unassignedJobs}
                            </span>
                          )}
                          {item.to === "/pricebook" && lowStockCount > 0 && (
                            <span
                              data-testid="sidebar-pricebook-low-stock-badge"
                              title={`${lowStockCount} material${lowStockCount === 1 ? "" : "s"} below reorder threshold`}
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white shadow-sm shadow-rose-900/40"
                            >
                              {lowStockCount}
                            </span>
                          )}
                          {item.badge && (
                            <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-br from-fuchsia-500 to-blue-500 text-white">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3 space-y-0.5">
        <button
          onClick={toggleHelpChat}
          data-testid="sidebar-help-support-btn"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
        >
          <HelpCircle className="h-[18px] w-[18px] text-slate-400" strokeWidth={2} />
          Help & Support
        </button>
        {!mobile && isTechnician && (
          <button
            onClick={onSidebarLogout}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="h-[18px] w-[18px] text-slate-400" strokeWidth={2} />
            Logout
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      <aside className="hidden md:flex w-64 shrink-0 bg-[#0B1E3F] text-slate-200 flex-col h-screen sticky top-0" data-testid="sidebar-desktop">
        {navBody()}
      </aside>

      {/* Mobile slide-in nav drawer (opened by the Topbar hamburger) */}
      <div
        className={`md:hidden fixed inset-0 z-50 ${mobileOpen ? "" : "pointer-events-none"}`}
        data-testid="mobile-nav-root"
        aria-hidden={!mobileOpen}
      >
        <div
          className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${mobileOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-nav-backdrop"
        />
        <aside
          className={`absolute left-0 top-0 h-full w-72 max-w-[82%] bg-[#0B1E3F] text-slate-200 flex flex-col shadow-2xl transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="mobile-nav-drawer"
        >
          <button
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-nav-close"
            aria-label="Close menu"
            className="absolute top-4 right-3 h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-300 z-10"
          >
            <X className="h-5 w-5" />
          </button>
          {navBody(() => setMobileOpen(false), true)}
        </aside>
      </div>
    </>
  );
}
